import {
	ReactElement,
	ReadonlyRefObject,
	useState,
	useEffect
} from "react";
import { Coordinate } from "client/Misc/commonTypes";
import styles from "./Moveable.module.less";
import { useSynchronizedRef } from "client/Hooks";

export type MoveableProps = {
	relativeTo?: HTMLElement;
	onStart(startPosition: Coordinate): void;
	onMove(offsetFromStart: Coordinate, currentPosition: Coordinate): void;
	onEnd?: () => void;
};

export const Moveable = (props: MoveableProps): ReactElement => {
	const {
		relativeTo = document.body,
		onStart,
		onMove,
		onEnd
	} = props;

	const [startPosition, setStartPosition] = useState<Coordinate | null>(null);
	const [relativeElementOffset, setRelativeElementOffset] = useState<Coordinate>({x: 0, y: 0});

	const onEndRef: ReadonlyRefObject<typeof onEnd> = useSynchronizedRef(onEnd);

	useEffect(
		() => {
			const relativeElementRect: DOMRect = relativeTo.getBoundingClientRect();
			setRelativeElementOffset({
				x: relativeElementRect.left,
				y: relativeElementRect.top
			});
		},
		[relativeTo]
	);

	const mouseDownHandler = (mouseEvent: React.MouseEvent): void => {
		const startPosition: Coordinate = {
			x: mouseEvent.clientX - relativeElementOffset.x,
			y: mouseEvent.clientY - relativeElementOffset.y
		};

		setStartPosition(startPosition);
		onStart(startPosition);
	};

	useEffect(
		(): (() => void) => {
			const mouseMoveHandler = (mouseEvent: MouseEvent): void => {
				if (!startPosition) {
					return;
				}

				mouseEvent.preventDefault();

				const movePosition: Coordinate = {
					x: mouseEvent.clientX - relativeElementOffset.x,
					y: mouseEvent.clientY - relativeElementOffset.y
				};

				const offset: Coordinate = {
					x: movePosition.x - startPosition.x,
					y: movePosition.y - startPosition.y
				};

				onMove(offset, movePosition);
			};

			const mouseUpHandler = (): void => {
				setStartPosition(null);
				onEndRef.current?.();
			};

			document.addEventListener("mousemove", mouseMoveHandler);
			document.addEventListener("mouseup", mouseUpHandler);

			return (): void => {
				document.removeEventListener("mousemove", mouseMoveHandler);
				document.removeEventListener("mouseup", mouseUpHandler);
			};
		},
		[
			onMove,
			relativeElementOffset,
			startPosition,
			onEndRef
		]
	);

	return (
		<div
			className={styles.moveable}
			onMouseDown={mouseDownHandler}
		/>
	);
};

Moveable.displayName = "Moveable";